import type { VaultClaim } from '@/lib/api/vault';
import { CLAIM_MAX_ATTEMPTS, claimDelayAfter, type CheckoutPending } from './model';

export interface ClaimWatchDeps {
  /** The outstanding purchase marker (already expiry-checked), or null. */
  readonly pending: () => CheckoutPending | null;
  readonly hasToken: () => boolean;
  readonly claim: () => Promise<VaultClaim | null>;
  /** Every successful claim body is applied to the funnel state. */
  readonly onClaim: (claim: VaultClaim) => void;
  readonly clearPending: () => void;
  readonly wait: (ms: number, signal: AbortSignal) => Promise<void>;
}

export type ClaimWatchResult = 'idle' | 'owned' | 'exhausted' | 'aborted';

/**
 * §5.4.2: after returning from checkout, ask the server to claim paid orders now instead of
 * waiting for its throttle. Polls until the pending rung is owned or 12 attempts have been made
 * (a failed claim counts), then removes the marker. Nothing happens without a marker and a token.
 */
export async function watchClaim(deps: ClaimWatchDeps, signal: AbortSignal): Promise<ClaimWatchResult> {
  const pending = deps.pending();
  if (pending === null || !deps.hasToken()) return 'idle';
  for (let attempt = 1; ; attempt += 1) {
    const claim = await deps.claim();
    if (signal.aborted) return 'aborted';
    if (claim !== null) deps.onClaim(claim);
    const doc = (claim?.documents as Record<string, { owned?: boolean } | undefined> | undefined)?.[pending.sku];
    if (doc?.owned === true) {
      deps.clearPending();
      return 'owned';
    }
    if (attempt >= CLAIM_MAX_ATTEMPTS) {
      deps.clearPending();
      return 'exhausted';
    }
    await deps.wait(claimDelayAfter(attempt), signal);
    if (signal.aborted) return 'aborted';
  }
}

/** A cancellable timer for `watchClaim`. */
export function abortableWait(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve) => {
    if (signal.aborted) {
      resolve();
      return;
    }
    const timer = window.setTimeout(done, ms);
    function done() {
      signal.removeEventListener('abort', done);
      window.clearTimeout(timer);
      resolve();
    }
    signal.addEventListener('abort', done);
  });
}
